import { OnboardingFeature } from 'src/app/services/onboarding/onboarding.types';

export type GettingStartedItemKey =
  | 'service'
  | 'incident'
  | 'monitor'
  | 'statuspage'
  | 'notifications';

export interface GettingStartedItem {
  key: GettingStartedItemKey;
  done: boolean;
}

/** What the dashboard knows about the workspace; null while still loading. */
export interface GettingStartedFacts {
  serviceCount: number | null;
  incidentCreated: boolean | null;
  monitorCount: number | null;
  statuspageCount: number | null;
  /** Push in a browser or the mobile app is set up (email always is). */
  pushSetUp: boolean | null;
  /** Picked on onboarding's Features screen; their items come first. */
  features: readonly OnboardingFeature[];
}

const FEATURE_OF: Partial<Record<GettingStartedItemKey, OnboardingFeature>> = {
  service: 'incident-management',
  incident: 'incident-management',
  monitor: 'uptime-monitoring',
  statuspage: 'status-pages',
};

const ORDER: readonly GettingStartedItemKey[] = [
  'service',
  'incident',
  'monitor',
  'statuspage',
];

/**
 * The "Get started" items in display order, or null until every fact has
 * loaded. Completion comes from real data. Items for features the user
 * picked move to the front, keeping their relative order.
 */
export function gettingStartedItems(
  facts: GettingStartedFacts
): GettingStartedItem[] | null {
  const {
    serviceCount,
    incidentCreated,
    monitorCount,
    statuspageCount,
    pushSetUp,
  } = facts;
  if (
    serviceCount === null ||
    incidentCreated === null ||
    monitorCount === null ||
    statuspageCount === null ||
    pushSetUp === null
  ) {
    return null;
  }
  const done: Record<GettingStartedItemKey, boolean> = {
    service: serviceCount > 0,
    incident: incidentCreated,
    monitor: monitorCount > 0,
    statuspage: statuspageCount > 0,
    notifications: pushSetUp,
  };
  const picked = (key: GettingStartedItemKey) => {
    const feature = FEATURE_OF[key];
    return !!feature && facts.features.includes(feature);
  };
  const keys: GettingStartedItemKey[] = [
    ...ORDER.filter(picked),
    ...ORDER.filter(key => !picked(key)),
    'notifications',
  ];
  return keys.map(key => ({ key, done: done[key] }));
}
